import {useEffect} from "react";
import {
    Navbar,
    Header,
    Footer,
    GlobalLoader,
    GlobalAlert,
    SystemAlertBar,
    VersionUpdateManager,
    IosAppPrompt,
} from "@/components";
import {Navigate, Route, Routes} from "react-router-dom";
import GlobalStyle, {
    AppLayout,
    AppMain,
    AppContent,
    AppScroll,
    RoutesWrap,
    FooterWrap,
    ScrollInner
} from "./globalStyles";
import HomePage from "./pages/HomePage/HomePage";
import UploadsPage from "./pages/UploadsPage/UploadsPage";
import InProgressUploadsPage from "./pages/InProgressUploadsPage/InProgressUploadsPage";
import SignInPage from "./pages/AuthPages/SIgnInPage";
import ForgotPasswordPage from "./pages/AuthPages/ForgotPasswordPage";
import PasswordSentPage from "./pages/AuthPages/PasswordSentPage";
import RoleProtectedRoute from "./components/AuthForm/RoleProtectedRoute";
import AddUserPage from "./pages/AddUserPage/UserManagementPage";
import {useModalManager} from "./components/hooks/useModalManager";
import ClipPage from "./pages/ClipPage/ClipsPage";
import {CssBaseline} from "@mui/material";
import {
    useAuthState,
    useListenToAuthChanges,
    useMobileMenuActions,
    useSystemAlertActions,
    useSystemSettingsActions,
} from "@/store";
import ChangePasswordGroup from "./components/Profile/ChangePassword/ChangePasswordGroup";
import AddNewUserPage from "./pages/AddUserPage/AddNewUserPage";
import PrivacyPolicyPage from "./pages/LegalPages/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/LegalPages/TermsOfUsePage";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import SystemAlertPage from "./pages/Admin/SystemAlertPage";
import AdminSettingsPage from "./pages/Admin/AdminSettingsPage";
import AdminUploadsLivePage from "./pages/Admin/AdminUploadsLivePage";
import BulkEmailPage from "./pages/Admin/BulkEmailPage";
import { ResumePreviousUploadBanner } from "./components/UploadProgress/ResumePreviousUploadBanner";
import { UploadConflictDialog } from "./components/UploadProgress/UploadConflictDialog";
import { CrossDeviceUploadsWatcher } from "./components/UploadProgress/CrossDeviceUploadsWatcher";
import { AutoResumeWatcher } from "./components/UploadProgress/AutoResumeWatcher";
import { AutoPausedNoticeManager } from "./components/UploadProgress/AutoPausedNoticeManager";
import { NetworkRecoveryWatcher } from "./components/UploadProgress/NetworkRecoveryWatcher";
import E2EAuthPage from "./pages/E2E/E2EAuthPage";
import TutorialsPage from "./tutorials/TutorialsPage";
import { TutorialPromptManager } from "./tutorials/TutorialPromptManager";
import { bootstrapObservability } from "./observability";
import { useObservabilityIdentity } from "./observability/useObservabilityIdentity";

function App() {
    const {currentUser} = useAuthState();

    useListenToAuthChanges();
    useObservabilityIdentity();

    const {setMobileMenuOpen} = useMobileMenuActions();
    const {fetchSystemAlert} = useSystemAlertActions();
    const {fetchActiveUploader, fetchAnalyticsEnabled} = useSystemSettingsActions();

    useEffect(() => {
        // Fire-and-forget: observability init tolerates failure (falls back to
        // Noop). Placed at top-level so it runs once regardless of auth state.
        bootstrapObservability();
    }, []);

    useEffect(() => {
        fetchSystemAlert();
    }, [fetchSystemAlert]);

    // Pull the uploader kill-switch as soon as the user is authenticated — the endpoint is
    // auth-gated, so we can't fetch before currentUser is set. Until this resolves, callers
    // use the cached value from localStorage (or the conservative legacy default).
    useEffect(() => {
        if (currentUser) {
            fetchActiveUploader();
        }
    }, [currentUser, fetchActiveUploader]);

    // Refresh the env-wide analytics setting so a subsequent reload picks up any admin
    // change. The current tab has already booted analytics using the localStorage cache
    // (see observability/analytics/bootstrap.js); this fetch keeps the cache in sync for
    // the next boot without touching the currently-running PostHog session.
    useEffect(() => {
        if (currentUser) {
            fetchAnalyticsEnabled();
        }
    }, [currentUser, fetchAnalyticsEnabled]);

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const {showChangePasswordModal, toggleChangePasswordModal} =
        useModalManager(closeMobileMenu);

    const {showAddUserModal, toggleAddUserdModal} =
        useModalManager(closeMobileMenu);

    const handleClose = (toggleModal) => {
        toggleModal();
        setMobileMenuOpen(false);
    };

    return (
        <>
            <CssBaseline/>
            <GlobalStyle/>
            <GlobalAlert/>
            <GlobalLoader/>
            <ScrollToTop/>
            <AppLayout>
                {showChangePasswordModal && (
                    <ChangePasswordGroup
                        show={showChangePasswordModal}
                        handleClose={() => handleClose(toggleChangePasswordModal)}
                    />
                )}

                {showAddUserModal && (
                    <AddNewUserPage
                        show={showAddUserModal}
                        handleClose={() => handleClose(toggleAddUserdModal)}
                    />
                )}

                <SystemAlertBar/>
                <VersionUpdateManager />
                <IosAppPrompt/>

                <AppMain>
                    {currentUser && <Navbar/>}
                    <AppContent>
                        {currentUser && (
                            <Header onOpenChangePassword={toggleChangePasswordModal}/>
                        )}
                        {currentUser && <ResumePreviousUploadBanner/>}
                        {currentUser && <CrossDeviceUploadsWatcher/>}
                        {currentUser && <AutoResumeWatcher/>}
                        {currentUser && <AutoPausedNoticeManager/>}
                        {currentUser && <NetworkRecoveryWatcher/>}
                        {currentUser && <UploadConflictDialog/>}
                        {currentUser && <TutorialPromptManager/>}

                        <AppScroll id="app-scroll">
                            <ScrollInner>
                                <RoutesWrap>
                                    <Routes>
                                        <Route
                                            path="/"
                                            element={
                                                <RoleProtectedRoute
                                                    allowedRoles={["contributor", "manager", "admin"]}
                                                >
                                                    <HomePage currentUser={currentUser}/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/uploads"
                                            element={
                                                <RoleProtectedRoute
                                                    allowedRoles={["contributor", "manager", "admin"]}
                                                >
                                                    <UploadsPage currentUser={currentUser}/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/uploads/in-progress"
                                            element={
                                                <RoleProtectedRoute
                                                    allowedRoles={["contributor", "manager", "admin"]}
                                                >
                                                    <InProgressUploadsPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/clips"
                                            element={
                                                <RoleProtectedRoute
                                                    allowedRoles={["contributor", "manager", "admin"]}
                                                >
                                                    <ClipPage currentUser={currentUser}/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/manageUsers"
                                            element={
                                                <RoleProtectedRoute allowedRoles={["manager", "admin"]}>
                                                    <AddUserPage onOpenAddUser={toggleAddUserdModal}/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/signin"
                                            element={
                                                <RoleProtectedRoute allowUnauthenticated={true}>
                                                    <SignInPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/resetpassword"
                                            element={
                                                <RoleProtectedRoute allowUnauthenticated={true}>
                                                    <ForgotPasswordPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/passwordsent"
                                            element={
                                                <RoleProtectedRoute allowUnauthenticated={true}>
                                                    <PasswordSentPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />

                                        <Route
                                            path="/tutorials"
                                            element={
                                                <RoleProtectedRoute
                                                    allowedRoles={["contributor", "manager", "admin"]}
                                                >
                                                    <TutorialsPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route path="/privacy-policy" element={<PrivacyPolicyPage/>}/>
                                        <Route path="/terms-of-use" element={<TermsOfUsePage/>}/>
                                        <Route
                                            path="/admin/alerts"
                                            element={
                                                <RoleProtectedRoute allowedRoles={["admin"]}>
                                                    <SystemAlertPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/settings"
                                            element={
                                                <RoleProtectedRoute allowedRoles={["admin"]}>
                                                    <AdminSettingsPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/uploader"
                                            element={<Navigate to="/admin/settings" replace/>}
                                        />
                                        <Route
                                            path="/admin/uploads"
                                            element={
                                                <RoleProtectedRoute allowedRoles={["admin"]}>
                                                    <AdminUploadsLivePage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/emails"
                                            element={
                                                <RoleProtectedRoute allowedRoles={["admin"]}>
                                                    <BulkEmailPage/>
                                                </RoleProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/__e2e__/auth"
                                            element={<E2EAuthPage />}
                                        />
                                    </Routes>
                                </RoutesWrap>

                                <FooterWrap>
                                    <Footer/>
                                </FooterWrap>
                            </ScrollInner>
                        </AppScroll>
                    </AppContent>
                </AppMain>
            </AppLayout>
        </>
    );
}

export default App;
